import type {
  TerraUIMessage} from '@terra/shared-dto-chat-types';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type MentionEntity,
} from '@terra/shared-dto-chat-types';
import type { CoPilotTraceExportPayload } from './co-pilot-trace-export';
import {
  sanitizeCoPilotExportCsvCell,
  sanitizeCoPilotExportMarkdownPlainText,
} from './co-pilot-export-sanitize';
import { formatCoPilotTranscriptHeader } from './co-pilot-transcript-export';

export const COPILOT_SESSION_OBJECT_KIND = {
  FINDING: 'Finding',
  SIGNAL: 'Signal',
  ENDPOINT: 'EndPoint',
  TRACE: 'Trace',
  WEB_PAGE: 'WebPage',
  SKILL: 'Skill',
} as const;

export type CoPilotSessionObjectKind =
  (typeof COPILOT_SESSION_OBJECT_KIND)[keyof typeof COPILOT_SESSION_OBJECT_KIND];

export type CoPilotSessionObjectSource = 'mentioned' | 'created' | 'surfaced';

export type CoPilotSessionObjectRef = {
  objectKind: CoPilotSessionObjectKind;
  objectId: string;
  label: string;
  source: CoPilotSessionObjectSource;
  timestamp?: string;
};

type SessionRecord = {
  recordType?: string;
  operation?: string;
  id?: string;
  title?: string;
  timestamp?: string;
};

const EXPORTABLE_MENTION_TYPES = new Set<MentionEntity['entityType']>([
  'Finding',
  'Signal',
  'WebPage',
  'EndPoint',
  'Trace',
  'Skill',
]);

function mapRecordTypeToObjectKind(
  recordType: string | undefined,
): CoPilotSessionObjectKind | null {
  if (!recordType) {
    return null;
  }

  const normalized = recordType.toUpperCase();
  if (normalized === 'FINDING') {
    return COPILOT_SESSION_OBJECT_KIND.FINDING;
  }
  if (normalized === 'SIGNAL') {
    return COPILOT_SESSION_OBJECT_KIND.SIGNAL;
  }
  if (normalized === 'TRACE' || normalized === 'EXECUTION') {
    return COPILOT_SESSION_OBJECT_KIND.TRACE;
  }
  if (normalized === 'ENDPOINT' || normalized === 'END_POINT') {
    return COPILOT_SESSION_OBJECT_KIND.ENDPOINT;
  }
  return null;
}

function mapMentionEntityType(
  entityType: MentionEntity['entityType'],
): CoPilotSessionObjectKind | null {
  if (!EXPORTABLE_MENTION_TYPES.has(entityType)) {
    return null;
  }

  return entityType as CoPilotSessionObjectKind;
}

function mapOperationToSource(
  operation: string | undefined,
): CoPilotSessionObjectSource {
  if (operation?.toUpperCase() === 'CREATE') {
    return 'created';
  }
  return 'mentioned';
}

function parseSessionRecordFromText(text: string): SessionRecord | null {
  const marker = '**SESSION_RECORD:**';
  const markerIndex = text.indexOf(marker);
  if (markerIndex === -1) {
    return null;
  }

  const jsonStart = text.indexOf('{', markerIndex);
  if (jsonStart === -1) {
    return null;
  }

  let depth = 0;
  for (let index = jsonStart; index < text.length; index += 1) {
    const char = text[index];
    if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        const candidate = text.slice(jsonStart, index + 1);
        try {
          return JSON.parse(candidate) as SessionRecord;
        } catch {
          return null;
        }
      }
    }
  }

  return null;
}

function readObjectContextMentionRef(
  mention: MentionEntity,
): Pick<CoPilotSessionObjectRef, 'objectKind' | 'objectId' | 'label'> | null {
  if (mention.entityType !== 'ObjectContext') {
    return null;
  }

  const objectContext = readRecord(mention.extraData?.objectContext);
  if (!objectContext) {
    return null;
  }

  const objectKind = mapPortalObjectKind(
    readStringField(objectContext, 'objectKind') ?? '',
  );
  const objectId = readStringField(objectContext, 'objectId');
  if (!objectKind || !objectId) {
    return null;
  }

  const objectLabel = readStringField(objectContext, 'objectLabel') ?? objectId;
  return {
    objectKind,
    objectId,
    label: mention.label?.trim() || `${objectKind} · ${objectLabel}`,
  };
}

function mapPortalObjectKind(value: string): CoPilotSessionObjectKind | null {
  if (value === 'Finding') {
    return COPILOT_SESSION_OBJECT_KIND.FINDING;
  }
  if (value === 'Signal') {
    return COPILOT_SESSION_OBJECT_KIND.SIGNAL;
  }
  if (value === 'EndPoint') {
    return COPILOT_SESSION_OBJECT_KIND.ENDPOINT;
  }
  if (value === 'WebPage') {
    return COPILOT_SESSION_OBJECT_KIND.WEB_PAGE;
  }
  if (value === 'Trace') {
    return COPILOT_SESSION_OBJECT_KIND.TRACE;
  }
  if (value === 'Skill') {
    return COPILOT_SESSION_OBJECT_KIND.SKILL;
  }
  return null;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readStringField(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readTraceMentionLabel(mention: MentionEntity): string {
  const signalDescription = readStringField(
    readRecord(mention.extraData) ?? {},
    'signalDescription',
  );
  if (signalDescription) {
    return `Execution: ${signalDescription}`;
  }

  return mention.label;
}

export function resolveCoPilotSessionObjectDisplayLabel({
  object,
  hydrated = {},
}: {
  object: CoPilotSessionObjectRef;
  hydrated?: Record<string, string>;
}): string {
  if (object.objectKind === COPILOT_SESSION_OBJECT_KIND.TRACE) {
    const hydratedTitle = hydrated.title?.trim();
    if (hydratedTitle) {
      return hydratedTitle.startsWith('Execution:')
        ? hydratedTitle
        : `Execution: ${hydratedTitle}`;
    }
    return object.label;
  }

  return hydrated.title?.trim() || object.label;
}

function extractRefsFromToolOutput({
  toolName,
  output,
  timestamp,
}: {
  toolName: string;
  output: unknown;
  timestamp?: string;
}): CoPilotSessionObjectRef[] {
  const refs: CoPilotSessionObjectRef[] = [];
  const normalizedToolName = toolName.replace(/_/g, '-').toLowerCase();

  const record = readRecord(output);
  if (record) {
    if (record.type === 'portal_object_presentation') {
      const objectKind = readStringField(record, 'objectKind');
      const objectId = readStringField(record, 'objectId');
      if (objectKind && objectId) {
        const mappedKind = mapPortalObjectKind(objectKind);
        if (mappedKind) {
          refs.push({
            objectKind: mappedKind,
            objectId,
            label: readStringField(record, 'title') ?? objectId,
            source: 'surfaced',
            timestamp,
          });
        }
      }
    }

    if (normalizedToolName === 'finding-tool') {
      const findingId =
        readStringField(record, 'findingId') ?? readStringField(record, 'id');
      if (findingId) {
        refs.push({
          objectKind: COPILOT_SESSION_OBJECT_KIND.FINDING,
          objectId: findingId,
          label: readStringField(record, 'title') ?? findingId,
          source: 'created',
          timestamp,
        });
      }
    }

    if (normalizedToolName === 'signal-tool') {
      const signalId =
        readStringField(record, 'signalId') ?? readStringField(record, 'id');
      if (signalId) {
        const operation = readStringField(record, 'operation');
        refs.push({
          objectKind: COPILOT_SESSION_OBJECT_KIND.SIGNAL,
          objectId: signalId,
          label: readStringField(record, 'title') ?? signalId,
          source: mapOperationToSource(operation),
          timestamp,
        });
      }
    }
  }

  const outputText =
    typeof output === 'string'
      ? output
      : record
        ? JSON.stringify(record)
        : null;
  if (!outputText) {
    return refs;
  }

  const sessionRecord = parseSessionRecordFromText(outputText);
  if (sessionRecord?.id) {
    const objectKind = mapRecordTypeToObjectKind(sessionRecord.recordType);
    if (objectKind) {
      refs.push({
        objectKind,
        objectId: sessionRecord.id,
        label: sessionRecord.title ?? sessionRecord.id,
        source: mapOperationToSource(sessionRecord.operation),
        timestamp: sessionRecord.timestamp ?? timestamp,
      });
    }
  }

  if (refs.length > 0) {
    return refs;
  }

  if (
    normalizedToolName !== 'finding-tool' &&
    normalizedToolName !== 'signal-tool'
  ) {
    return refs;
  }

  try {
    const parsed = JSON.parse(outputText) as Record<string, unknown>;
    const objectId =
      normalizedToolName === 'finding-tool'
        ? (readStringField(parsed, 'findingId') ??
          readStringField(parsed, 'id'))
        : (readStringField(parsed, 'signalId') ??
          readStringField(parsed, 'id'));
    if (!objectId) {
      return refs;
    }

    refs.push({
      objectKind:
        normalizedToolName === 'finding-tool'
          ? COPILOT_SESSION_OBJECT_KIND.FINDING
          : COPILOT_SESSION_OBJECT_KIND.SIGNAL,
      objectId,
      label: readStringField(parsed, 'title') ?? objectId,
      source:
        normalizedToolName === 'finding-tool'
          ? 'created'
          : mapOperationToSource(readStringField(parsed, 'operation')),
      timestamp,
    });
  } catch {
    return refs;
  }

  return refs;
}

function collectToolOutputRefs({
  objects,
  toolName,
  output,
  timestamp,
}: {
  objects: Map<string, CoPilotSessionObjectRef>;
  toolName: string;
  output: unknown;
  timestamp?: string;
}): void {
  for (const ref of extractRefsFromToolOutput({
    toolName,
    output,
    timestamp,
  })) {
    upsertObjectRef({ objects, ref });
  }
}

function mergeObjectSource(
  existing: CoPilotSessionObjectSource,
  incoming: CoPilotSessionObjectSource,
): CoPilotSessionObjectSource {
  const sourceRank: Record<CoPilotSessionObjectSource, number> = {
    mentioned: 0,
    surfaced: 1,
    created: 2,
  };
  return sourceRank[incoming] > sourceRank[existing] ? incoming : existing;
}

function upsertObjectRef({
  objects,
  ref,
}: {
  objects: Map<string, CoPilotSessionObjectRef>;
  ref: CoPilotSessionObjectRef;
}): void {
  const key = `${ref.objectKind}:${ref.objectId}`;
  const existing = objects.get(key);
  if (!existing) {
    objects.set(key, ref);
    return;
  }

  objects.set(key, {
    ...existing,
    source: mergeObjectSource(existing.source, ref.source),
    label: existing.label || ref.label,
    timestamp: existing.timestamp ?? ref.timestamp,
  });
}

export function extractCoPilotSessionObjectRefs({
  messages,
  exportPayload,
}: {
  messages: TerraUIMessage[];
  exportPayload: CoPilotTraceExportPayload;
}): CoPilotSessionObjectRef[] {
  const objects = new Map<string, CoPilotSessionObjectRef>();

  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type !== TerraUIMessageDataPartName) {
        continue;
      }

      const data = part.data;
      if (data.type === DataMessageTypeEnum.Mentions) {
        for (const mention of data.mentions) {
          const objectContextRef = readObjectContextMentionRef(mention);
          if (objectContextRef) {
            upsertObjectRef({
              objects,
              ref: {
                ...objectContextRef,
                source: 'mentioned',
              },
            });
            continue;
          }

          const objectKind = mapMentionEntityType(mention.entityType);
          if (!objectKind) {
            continue;
          }

          upsertObjectRef({
            objects,
            ref: {
              objectKind,
              objectId: mention.entityId,
              label:
                objectKind === COPILOT_SESSION_OBJECT_KIND.TRACE
                  ? readTraceMentionLabel(mention)
                  : mention.label,
              source: 'mentioned',
            },
          });
        }
        continue;
      }

      if (data.type === DataMessageTypeEnum.ToolCallComplete) {
        collectToolOutputRefs({
          objects,
          toolName: data.toolName,
          output: data.output,
          timestamp:
            typeof data.timestamp === 'number'
              ? new Date(data.timestamp).toISOString()
              : undefined,
        });
      }
    }
  }

  for (const entry of exportPayload.timeline) {
    if (entry.kind !== 'tool') {
      continue;
    }

    collectToolOutputRefs({
      objects,
      toolName: entry.toolName,
      output: entry.output,
      timestamp: entry.timestamp,
    });
  }

  return Array.from(objects.values());
}

export function filterCoPilotSessionObjectRefs({
  objects,
  objectKinds,
  sources,
}: {
  objects: CoPilotSessionObjectRef[];
  objectKinds?: CoPilotSessionObjectKind[];
  sources?: CoPilotSessionObjectSource[];
}): CoPilotSessionObjectRef[] {
  return objects.filter((object) => {
    if (
      objectKinds &&
      objectKinds.length > 0 &&
      !objectKinds.includes(object.objectKind)
    ) {
      return false;
    }
    if (sources && sources.length > 0 && !sources.includes(object.source)) {
      return false;
    }
    return true;
  });
}

export function countCoPilotSessionObjectsCsvRows(csv: string): number {
  const lines = csv.trim().split('\n');
  return Math.max(0, lines.length - 1);
}

export function buildCoPilotSessionObjectsCsv({
  objects,
  hydratedRows,
}: {
  objects: CoPilotSessionObjectRef[];
  hydratedRows: Record<string, string>[];
}): string {
  const headers = [
    'objectKind',
    'objectId',
    'label',
    'source',
    'timestamp',
    'severity',
    'status',
    'title',
    'category',
    'url',
  ];

  const lines = [headers.join(',')];

  objects.forEach((object, index) => {
    const hydrated = hydratedRows[index] ?? {};
    const row = [
      object.objectKind,
      object.objectId,
      resolveCoPilotSessionObjectDisplayLabel({ object, hydrated }),
      object.source,
      object.timestamp ?? '',
      hydrated.severity ?? '',
      hydrated.status ?? '',
      hydrated.title ?? '',
      hydrated.category ?? '',
      hydrated.url ?? '',
    ].map((cell) => sanitizeCoPilotExportCsvCell(String(cell)));
    lines.push(row.join(','));
  });

  return lines.join('\n');
}

function escapeMarkdownInline(value: string): string {
  return sanitizeCoPilotExportMarkdownPlainText(value);
}

function formatObjectMarkdownField({
  label,
  value,
}: {
  label: string;
  value: string;
}): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  return `- **${label}:** ${escapeMarkdownInline(trimmed)}`;
}

function formatObjectMarkdownSection({
  object,
  hydrated,
}: {
  object: CoPilotSessionObjectRef;
  hydrated: Record<string, string>;
}): string {
  const displayLabel = resolveCoPilotSessionObjectDisplayLabel({
    object,
    hydrated,
  });
  const heading =
    object.objectKind === COPILOT_SESSION_OBJECT_KIND.TRACE &&
    displayLabel.startsWith('Execution:')
      ? `### ${escapeMarkdownInline(displayLabel)}`
      : `### ${escapeMarkdownInline(object.objectKind)}: ${escapeMarkdownInline(displayLabel)}`;
  const fields = [
    formatObjectMarkdownField({ label: 'Object ID', value: object.objectId }),
    formatObjectMarkdownField({ label: 'Source', value: object.source }),
    formatObjectMarkdownField({
      label: 'Timestamp',
      value: object.timestamp ?? '',
    }),
    formatObjectMarkdownField({
      label: 'Severity',
      value: hydrated.severity ?? '',
    }),
    formatObjectMarkdownField({
      label: 'Status',
      value: hydrated.status ?? '',
    }),
    formatObjectMarkdownField({
      label: 'Category',
      value: hydrated.category ?? '',
    }),
    formatObjectMarkdownField({ label: 'URL', value: hydrated.url ?? '' }),
  ].filter((field): field is string => field !== null);

  return [heading, '', ...fields].join('\n');
}

export function buildCoPilotSessionObjectsMarkdown({
  title,
  threadId,
  objects,
  hydratedRows,
  exportedAt = new Date().toISOString(),
  timeZone,
}: {
  title: string | null | undefined;
  threadId: string;
  objects: CoPilotSessionObjectRef[];
  hydratedRows: Record<string, string>[];
  exportedAt?: string;
  timeZone?: string;
}): string {
  const header = formatCoPilotTranscriptHeader({
    title,
    threadId,
    exportedAt,
    timeZone,
  });
  const lines = [
    header,
    '',
    `Exported ${objects.length} session object${objects.length === 1 ? '' : 's'}.`,
    '',
  ];

  if (objects.length === 0) {
    return `${lines.join('\n')}\n`;
  }

  const sections = objects.map((object, index) =>
    formatObjectMarkdownSection({
      object,
      hydrated: hydratedRows[index] ?? {},
    }),
  );

  return `${lines.join('\n')}${sections.join('\n\n')}\n`;
}
